import { useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { buildPaginatedEndpoint } from '@/helpers/api'
import { SweetspotError } from '@/helpers/error'

import { promptsQueryKeys } from '@/hooks/prompts/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { SavedPrompt } from '@/types/library/SavedPrompt'

interface UseSavedPromptsArgs<TData = SavedPrompt[]> {
  orgId: UUID | ''
  select?: (data: SavedPrompt[]) => TData
}

export const fetchPrompts = async (
  orgId: UUID | '',
  accessToken: string | undefined | null,
  pagination: PaginateRequest,
): Promise<PaginatedResponse<SavedPrompt>> => {
  if (!orgId) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: 'No org ID provided.',
    })
  }
  if (accessToken === null || accessToken === undefined) {
    return { count: 0, has_more: false, data: [] }
  }
  const url = buildPaginatedEndpoint(
    `${API_URL}/api/v1/organizations/${orgId}/prompts`,
    pagination,
  )
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: { status: res.status },
    })
  }
  return (await res.json()) as PaginatedResponse<SavedPrompt>
}

export const usePrompts = <TData = SavedPrompt[]>({
  orgId,
  select,
}: UseSavedPromptsArgs<TData>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const memoizedSelect = useCallback(
    (data: PaginatedResponse<SavedPrompt>) =>
      select ? select(data.data) : (data.data as TData),
    [select],
  )

  return useQuery({
    queryFn: () => fetchPrompts(orgId, accessToken, { limit: 10000 }),
    queryKey: promptsQueryKeys.organization({ orgId }),
    staleTime: Infinity,
    enabled: !!orgId && !!accessToken,
    select: memoizedSelect,
  })
}

interface UseSavedPromptCountArgs {
  orgId: UUID | ''
}

interface SavedPromptCount {
  count: number
}

const selectSavedPromptCount = (
  data: PaginatedResponse<SavedPrompt>,
): SavedPromptCount => {
  return { count: data.count }
}

export const usePromptCount = ({ orgId }: UseSavedPromptCountArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  return useQuery({
    queryKey: promptsQueryKeys.count({ orgId }),
    queryFn: () => fetchPrompts(orgId, accessToken, { limit: 10000 }),
    enabled: !!orgId && !!accessToken,
    select: selectSavedPromptCount,
  })
}
